import { useState, useEffect } from 'react';
import { History, Package, Trash2, ArrowRight, DollarSign, Users, Filter, Calendar, ChevronDown, ChevronRight, RefreshCw, Search, X } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

// Action type labels and colors
const ACTION_CONFIG = {
    product_added: { label: 'Produs adăugat', color: 'bg-green-100 text-green-700', icon: Package },
    product_deleted: { label: 'Produs șters', color: 'bg-red-100 text-red-700', icon: Trash2 },
    product_moved: { label: 'Produs mutat', color: 'bg-blue-100 text-blue-700', icon: ArrowRight },
    price_changed: { label: 'Preț modificat', color: 'bg-amber-100 text-amber-700', icon: DollarSign },
    quantity_changed: { label: 'Cantitate modificată', color: 'bg-cyan-100 text-cyan-700', icon: Package },
    client_completed: { label: 'Client mutat în comandă', color: 'bg-purple-100 text-purple-700', icon: Users },
    modification_request: { label: 'Cerere modificare', color: 'bg-orange-100 text-orange-700', icon: History }
};

const ENTITY_LABELS = {
    reservation: 'Rezervare',
    order: 'Comandă',
    customer: 'Client',
    product: 'Produs',
    pallet: 'Palet'
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatMonthLabel = (monthKey) => {
    if (!monthKey) return '';
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' });
};

export default function IstoricMiscari() {
    const [activities, setActivities] = useState([]);
    const [groupedByMonth, setGroupedByMonth] = useState({});
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [expandedMonths, setExpandedMonths] = useState({});
    
    // Filters
    const [filters, setFilters] = useState({
        customer_id: '',
        action: '',
        entity_type: '',
        product: '',
        start_date: '',
        end_date: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    
    // Pagination
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const limit = 500;

    useEffect(() => {
        fetchActivities();
        fetchStats();
    }, [page]);

    const fetchActivities = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.customer_id) params.append('customer_id', filters.customer_id);
            if (filters.action) params.append('action', filters.action);
            if (filters.entity_type) params.append('entity_type', filters.entity_type);
            if (filters.product) params.append('product', filters.product);
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);
            params.append('limit', limit);
            params.append('skip', page * limit);
            
            const response = await api.get(`/activity-log?${params.toString()}`);
            setActivities(response.data.activities || []);
            setGroupedByMonth(response.data.grouped_by_month || {});
            setTotal(response.data.total || 0);
            
            // Expand all months by default
            const expanded = {};
            Object.keys(response.data.grouped_by_month || {}).forEach(month => {
                expanded[month] = true;
            });
            setExpandedMonths(expanded);
        } catch (err) {
            console.error(err);
            toast.error('Eroare la încărcarea istoricului');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await api.get('/activity-log/stats');
            setStats(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleSearch = () => {
        setPage(0);
        fetchActivities();
    };

    const handleClearFilters = () => {
        setFilters({
            customer_id: '',
            action: '',
            entity_type: '',
            product: '',
            start_date: '',
            end_date: ''
        });
        setPage(0);
        setTimeout(fetchActivities, 100);
    };

    const handleReconstruct = async () => {
        if (!window.confirm('Aceasta va reconstrui istoricul din datele existente. Continuați?')) return;
        
        try {
            toast.info('Se reconstruiește istoricul...');
            const response = await api.get('/activity-log/reconstruct');
            toast.success(response.data.message);
            fetchActivities();
            fetchStats();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.detail || 'Eroare la reconstrucție');
        }
    };

    const toggleMonth = (month) => {
        setExpandedMonths(prev => ({
            ...prev,
            [month]: !prev[month]
        }));
    };

    const renderActivityItem = (activity) => {
        const config = ACTION_CONFIG[activity.action] || { label: activity.action, color: 'bg-gray-100 text-gray-700', icon: History };
        const IconComponent = config.icon;
        const details = activity.details || {};
        
        return (
            <div 
                key={activity.id} 
                className="bg-white border border-zinc-200 rounded-lg px-3 py-2 hover:shadow-sm transition-shadow"
            >
                <div className="flex items-center gap-3">
                    {/* Icon */}
                    <div className={`p-1.5 rounded-lg ${config.color} flex-shrink-0`}>
                        <IconComponent size={14} />
                    </div>
                    
                    {/* Main content - single line */}
                    <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                        {/* Action badge */}
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${config.color} flex-shrink-0`}>
                            {config.label}
                        </span>
                        
                        {/* Product name */}
                        {details.product_name && (
                            <span className="font-medium text-zinc-800 text-sm truncate max-w-[200px]" title={details.product_name}>
                                {details.product_name}
                            </span>
                        )}
                        
                        {/* Quantity & Price inline */}
                        {details.quantity && !details.changes && (
                            <span className="text-zinc-500 text-sm">x{details.quantity}</span>
                        )}
                        {details.unit_price > 0 && !details.changes && (
                            <span className="text-teal-600 text-sm">@ {details.unit_price?.toFixed(2)}</span>
                        )}
                        
                        {/* Barcode */}
                        {details.barcode && (
                            <span className="text-xs text-zinc-400 font-mono bg-zinc-100 px-1.5 py-0.5 rounded">
                                {details.barcode}
                            </span>
                        )}
                        
                        {/* Total */}
                        {details.total_value > 0 && !details.changes && (
                            <span className="font-semibold text-teal-700 text-sm">
                                = {details.total_value?.toFixed(2)} RON
                            </span>
                        )}
                        
                        {/* Changes for price/quantity modifications */}
                        {details.changes && (
                            <span className="font-medium text-amber-700 text-sm">
                                {details.changes}
                            </span>
                        )}
                        
                        {/* Old/New total for modifications */}
                        {details.old_total !== undefined && details.new_total !== undefined && (
                            <span className="text-sm">
                                <span className="line-through text-zinc-400">{details.old_total?.toFixed(2)}</span>
                                <span className="text-teal-600 font-semibold ml-1">→ {details.new_total?.toFixed(2)} RON</span>
                            </span>
                        )}
                        
                        {/* Box number */}
                        {details.box_number && (
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-bold">
                                CUTIA {details.box_number}
                            </span>
                        )}
                        {details.original_box && !details.box_number && (
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                Cutia: {details.original_box}
                            </span>
                        )}
                        
                        {/* Direction for moves */}
                        {details.direction && (
                            <span className="text-blue-600 text-sm">{details.direction}</span>
                        )}
                        
                        {/* Returned to */}
                        {details.returned_to && (
                            <span className="text-amber-600 text-xs">→ {details.returned_to}</span>
                        )}
                        
                        {/* Items moved count */}
                        {details.items_moved && (
                            <span className="text-zinc-600 text-sm">{details.items_moved} produse</span>
                        )}
                        
                        {/* Customer badge */}
                        {activity.customer_id && (
                            <span className="px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-medium ml-auto flex-shrink-0">
                                {activity.customer_id}
                            </span>
                        )}
                    </div>
                    
                    {/* Timestamp - always visible */}
                    <div className="text-xs text-zinc-400 whitespace-nowrap flex-shrink-0 ml-2">
                        {formatDate(activity.created_at)}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-indigo-100 rounded-xl">
                        <History size={28} className="text-indigo-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-zinc-800">Istoric Mișcări</h1>
                        <p className="text-zinc-500 text-sm">
                            {total} activități înregistrate
                        </p>
                    </div>
                </div>
                
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                            showFilters ? 'bg-indigo-600 text-white' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                        }`}
                    >
                        <Filter size={16} />
                        Filtre
                    </button>
                    <button
                        onClick={fetchActivities}
                        className="px-4 py-2 bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200 flex items-center gap-2"
                    >
                        <RefreshCw size={16} />
                        Reîmprospătare
                    </button>
                    {total === 0 && (
                        <button
                            onClick={handleReconstruct}
                            className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 flex items-center gap-2"
                        >
                            <History size={16} />
                            Reconstruiește Istoric
                        </button>
                    )}
                </div>
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
                    {Object.entries(stats.by_action || {}).map(([action, count]) => {
                        const config = ACTION_CONFIG[action] || { label: action, color: 'bg-gray-100 text-gray-700' };
                        return (
                            <div key={action} className={`${config.color} rounded-lg p-3 text-center`}>
                                <p className="text-2xl font-bold">{count}</p>
                                <p className="text-xs">{config.label}</p>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Filters Panel */}
            {showFilters && (
                <div className="bg-white border border-zinc-200 rounded-xl p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-zinc-700 mb-1">Client</label>
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                                <input
                                    type="text"
                                    value={filters.customer_id}
                                    onChange={(e) => setFilters(prev => ({ ...prev, customer_id: e.target.value }))}
                                    placeholder="ID client..."
                                    className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-zinc-700 mb-1">Produs</label>
                            <div className="relative">
                                <Package size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                                <input
                                    type="text"
                                    value={filters.product}
                                    onChange={(e) => setFilters(prev => ({ ...prev, product: e.target.value }))}
                                    placeholder="Nume sau cod bare..."
                                    className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-zinc-700 mb-1">Tip acțiune</label>
                            <select
                                value={filters.action}
                                onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}
                                className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">Toate</option>
                                {Object.entries(ACTION_CONFIG).map(([key, val]) => (
                                    <option key={key} value={key}>{val.label}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-zinc-700 mb-1">Entitate</label>
                            <select
                                value={filters.entity_type}
                                onChange={(e) => setFilters(prev => ({ ...prev, entity_type: e.target.value }))}
                                className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">Toate</option>
                                {Object.entries(ENTITY_LABELS).map(([key, val]) => (
                                    <option key={key} value={key}>{val}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-zinc-700 mb-1">De la</label>
                            <input
                                type="date"
                                value={filters.start_date}
                                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                                className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-zinc-700 mb-1">Până la</label>
                            <input
                                type="date"
                                value={filters.end_date}
                                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                                className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                        <button
                            onClick={handleSearch}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                        >
                            <Search size={16} />
                            Caută
                        </button>
                        <button
                            onClick={handleClearFilters}
                            className="px-4 py-2 bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200 flex items-center gap-2"
                        >
                            <X size={16} />
                            Resetează
                        </button>
                    </div>
                </div>
            )}

            {/* Activities List - Grouped by Month */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
            ) : activities.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl border border-zinc-200">
                    <History size={48} className="mx-auto text-zinc-300 mb-4" />
                    <h3 className="text-lg font-semibold text-zinc-700">Nu există activități</h3>
                    <p className="text-zinc-500 mt-1">Activitățile vor apărea aici pe măsură ce faci modificări.</p>
                    <button
                        onClick={handleReconstruct}
                        className="mt-4 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 inline-flex items-center gap-2"
                    >
                        <History size={16} />
                        Reconstruiește din date existente
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {Object.entries(groupedByMonth).sort((a, b) => b[0].localeCompare(a[0])).map(([month, monthActivities]) => (
                        <div key={month} className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
                            {/* Month Header */}
                            <button
                                onClick={() => toggleMonth(month)}
                                className="w-full px-4 py-3 bg-zinc-50 hover:bg-zinc-100 flex items-center justify-between transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    {expandedMonths[month] ? (
                                        <ChevronDown size={20} className="text-zinc-400" />
                                    ) : (
                                        <ChevronRight size={20} className="text-zinc-400" />
                                    )}
                                    <Calendar size={18} className="text-indigo-500" />
                                    <span className="font-semibold text-zinc-700 capitalize">
                                        {formatMonthLabel(month)}
                                    </span>
                                </div>
                                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                                    {monthActivities.length} activități
                                </span>
                            </button>
                            
                            {/* Month Activities */}
                            {expandedMonths[month] && (
                                <div className="p-3 space-y-1.5">
                                    {monthActivities.map(activity => renderActivityItem(activity))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {total > limit && (
                <div className="flex items-center justify-center gap-4 mt-6">
                    <button
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="px-4 py-2 bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Anterior
                    </button>
                    <span className="text-zinc-600">
                        Pagina {page + 1} din {Math.ceil(total / limit)}
                    </span>
                    <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={(page + 1) * limit >= total}
                        className="px-4 py-2 bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Următor
                    </button>
                </div>
            )}
        </div>
    );
}
